import axios, { AxiosResponse } from 'axios'
import { CdaConsent, defaultCdaConsent } from '@/types/CdaConsent'
import isSubscribeNewsletter from '@/state/isSubscribeNewsletter'
import locale from '@/state/locale'
import { isCdaOptInEnabledForUser } from '@/logic/contact/isCdaOptInEnabled'
import { DEFAULT_CRM_BASE_URL } from '@/config/cdaOptIn'
import store from '@/store'

type OptinStatusResponse = {
  found: boolean
  email?: string
  optin_mail: boolean
  optin_sms: boolean
  optin_tracking_open: boolean
  optin_partenaires: boolean
  optin_whatsapp: boolean
  legitimate_interest: boolean
  optin_projet: boolean
  contract_consent: boolean
}

export type SaveCdaConsentExtras = {
  /** When email is turned off from Profile, also clear IL (Confluence unsubscribe rule). */
  clearLegitimateInterest?: boolean
  /** Optional iresa id for pilot gate (e.g. right after register). */
  userId?: string
}

const getCrmBaseUrl = (): string => {
  // Code default; env is optional override only.
  const fromEnv =
    (typeof window !== 'undefined' && window.env?.VUE_APP_CRM_BASE_URL) ||
    process.env.VUE_APP_CRM_BASE_URL
  const base = String(fromEnv || DEFAULT_CRM_BASE_URL).trim()
  return (base || DEFAULT_CRM_BASE_URL).replace(/\/$/, '')
}

const getWebsite = (): string => {
  if (typeof window !== 'undefined' && window.env?.VUE_APP_MENU_DOMAIN) {
    return String(window.env.VUE_APP_MENU_DOMAIN)
  }
  return String(process.env.VUE_APP_MENU_DOMAIN || 'www.travelski.com')
}

const getAcceptLanguage = (): string => {
  return (
    locale.value ||
    (typeof window !== 'undefined' ? window.env?.VUE_APP_LOCALE : undefined) ||
    'fr-FR'
  )
}

const getCurrentUserId = (): string | undefined => {
  return store.state.auth?.user?.iresa_id
}

/**
 * Prefetch CDA opt-in from Selligent and hydrate local consent state.
 * Soft-fail: returns false on error / not found / pilot gate.
 */
export const loadCdaConsentFromCrm = async (email?: string): Promise<boolean> => {
  if (!isCdaOptInEnabledForUser(getCurrentUserId())) return false
  if (!email) return false
  const url = `${getCrmBaseUrl()}/optin/status`
  try {
    const response: AxiosResponse<OptinStatusResponse> = await axios.get(url, {
      params: { email, website: getWebsite() },
      headers: { 'accept-language': getAcceptLanguage() },
    })
    const status = response.data
    if (!status?.found) return false

    isSubscribeNewsletter.value = {
      email: !!status.optin_mail,
      sms: !!status.optin_sms,
      trackingOpen: !!status.optin_tracking_open,
      partenaires: !!status.optin_partenaires,
      whatsapp: !!status.optin_whatsapp,
    }
    return true
  } catch (err) {
    console.error('[loadCdaConsentFromCrm]', url, err)
    return false
  }
}

/**
 * Persist full CDA consent via CRM /subscribe/v2 (same contract as booking-funnel).
 * Soft-fail: never throws to callers that must keep Auth/profile working.
 */
export const saveCdaConsent = async (
  email: string | undefined,
  consentOverride?: Partial<CdaConsent>,
  extras?: SaveCdaConsentExtras
): Promise<boolean> => {
  if (!isCdaOptInEnabledForUser(extras?.userId ?? getCurrentUserId())) {
    return false
  }
  if (!email) {
    console.warn('[saveCdaConsent] skipped: no email')
    return false
  }

  const consent: CdaConsent = {
    ...defaultCdaConsent(),
    ...isSubscribeNewsletter.value,
    ...consentOverride,
    whatsapp: false,
  }

  isSubscribeNewsletter.value = consent

  const payload: Record<string, unknown> = {
    email,
    website: getWebsite(),
    optin_mail: !!consent.email,
    optin_sms: !!consent.sms,
    optin_tracking_open: !!consent.trackingOpen,
    optin_partenaires: !!consent.partenaires,
    optin_whatsapp: false,
  }

  // Only when Profile explicitly clears (email turned off) — never on every save while email=0.
  if (extras?.clearLegitimateInterest) {
    payload.legitimate_interest = false
  }

  const url = `${getCrmBaseUrl()}/subscribe/v2`
  try {
    const response: AxiosResponse<boolean | string> = await axios.post(url, payload, {
      headers: {
        'accept-language': getAcceptLanguage(),
        'Content-Type': 'application/json',
      },
    })
    return !!response.data
  } catch (err) {
    console.error('[saveCdaConsent]', url, err)
    return false
  }
}

export default saveCdaConsent
